// Firebase client singleton — read env, initialize once, expose typed handles.
// Apps pass their VITE_FIREBASE_* env values into createFirebase().

import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  type UserCredential,
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  signInAnonymously,
  signInWithPopup,
} from "firebase/auth";
import {
  type Firestore,
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import {
  type FirebaseStorage,
  connectStorageEmulator,
  getStorage,
} from "firebase/storage";
import {
  type Functions,
  connectFunctionsEmulator,
  getFunctions,
} from "firebase/functions";

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface FirebaseHandles {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
}

export interface CreateFirebaseOptions {
  config: FirebaseConfig;
  /** When true (dev mode), connect to local emulators on default ports. */
  useEmulators?: boolean;
}

let cached: FirebaseHandles | null = null;

export function createFirebase({
  config,
  useEmulators = false,
}: CreateFirebaseOptions): FirebaseHandles {
  if (cached) return cached;

  const app = initializeApp(config);
  const auth = getAuth(app);
  // Day 14 #9 + #15: turn on Firestore offline persistence so writes queue
  // when learners drop signal in the hills and replay automatically on
  // reconnect. persistentMultipleTabManager is the safe default for PWAs
  // that may be opened in multiple tabs; in standalone mode it falls back
  // to single-tab behavior. Emulator mode keeps the in-memory cache —
  // persistent cache + emulator has rough edges.
  const db = useEmulators
    ? getFirestore(app)
    : initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
        // Activity-day hardening: the hills + venue wifi sometimes block
        // Firestore's WebSocket/streaming channel. Auto-detect probes the
        // connection on startup and silently falls back to long-polling
        // ONLY when streaming is blocked — normal networks keep streaming,
        // so this is invisible except where it rescues a hostile network.
        // (NOT experimentalForceLongPolling, which would slow every client.)
        experimentalAutoDetectLongPolling: true,
      });
  const storage = getStorage(app);
  const functions = getFunctions(app, "asia-east1");

  if (useEmulators) {
    connectAuthEmulator(auth, "http://127.0.0.1:9099", {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    connectStorageEmulator(storage, "127.0.0.1", 9199);
    connectFunctionsEmulator(functions, "127.0.0.1", 5001);
  }

  cached = { app, auth, db, storage, functions };
  return cached;
}

/** Get the previously-initialized handles. Throws if createFirebase() wasn't called. */
export function getFirebase(): FirebaseHandles {
  if (!cached) {
    throw new Error(
      "[@goodbye/firebase] createFirebase() must be called before getFirebase().",
    );
  }
  return cached;
}

// ── Auth sugar — thin wrappers so apps don't import firebase/auth directly.

/**
 * Reject if `p` doesn't settle within `ms`. Activity-day hardening: on a
 * weak signal Firebase's sign-in can hang with no surfaced error, leaving
 * the gate spinning forever. A timeout converts that into a normal error
 * the existing catch branches already render. Note: this does NOT cancel
 * the underlying call — if it later succeeds, signInAnonymously is
 * idempotent, so a reload (the error UI's action) picks up the session.
 */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label}逾時，請檢查網路後重試`)),
      ms,
    );
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/** Sign in anonymously (學員端 — Day 12). 10s timeout: pure network round-trip. */
export function signInAnon(): Promise<UserCredential> {
  return withTimeout(signInAnonymously(getFirebase().auth), 10_000, "匿名登入");
}

/**
 * Sign in with Google popup (工作員端 — Day 12). 90s timeout: the popup
 * waits on the worker picking an account / 2FA, so a short cap would kill
 * legitimate logins.
 */
export function signInGoogle(): Promise<UserCredential> {
  const provider = new GoogleAuthProvider();
  return withTimeout(
    signInWithPopup(getFirebase().auth, provider),
    90_000,
    "Google 登入",
  );
}
